import { useCallback, useEffect, useRef, useState } from "react";

function isoLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Fetches + caches per-day counts (for calendar badges) a month at a time,
 * widened a week each side so adjacent-month grid cells get badges too —
 * mirrors the old main.ts::loadCounts exactly. `cacheKeyPrefix` should
 * change whenever the counts would (feature/site switch) to drop stale
 * cached months.
 */
export function useDayCounts(
  fetchCounts: (from: string, to: string) => Promise<Record<string, number>>,
  cacheKeyPrefix: string,
) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const fetchedRef = useRef(new Set<string>());
  const fetchCountsRef = useRef(fetchCounts);
  fetchCountsRef.current = fetchCounts;

  useEffect(() => {
    fetchedRef.current = new Set();
    setCounts({});
  }, [cacheKeyPrefix]);

  const ensureMonth = useCallback(
    (year: number, month: number) => {
      const key = `${cacheKeyPrefix}-${year}-${month}`;
      if (fetchedRef.current.has(key)) return;
      fetchedRef.current.add(key);
      const from = isoLocal(new Date(year, month, 1 - 7));
      const to = isoLocal(new Date(year, month + 1, 0 + 7));
      fetchCountsRef.current(from, to)
        .then((c) => setCounts((prev) => ({ ...prev, ...c })))
        .catch(() => fetchedRef.current.delete(key));
    },
    [cacheKeyPrefix],
  );

  return { counts, ensureMonth };
}
